import { useState } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { ClientProfile, SubscriptionData } from '../types';

interface Props {
  profile: ClientProfile;
  subscription: SubscriptionData | null;
  onUpdate: (data: Partial<ClientProfile>) => void;
}

const subStatusColor: Record<string, string> = {
  active: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20',
  trial: 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/20',
  expired: 'bg-orange-500/20 text-orange-400 border border-orange-500/20',
  cancelled: 'bg-red-500/20 text-red-400 border border-red-500/20',
};

const subStatusLabel: Record<string, string> = {
  active: 'Assinatura Ativa',
  trial: 'Trial',
  expired: 'Vencida',
  cancelled: 'Cancelada',
};

export default function ClientHeader({ profile, subscription, onUpdate }: Props) {
  const [saving, setSaving] = useState(false);

  const initials = (() => {
    const n = profile.display_name || profile.full_name || profile.email || '';
    const parts = n.trim().split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (parts[0]?.[0] || 'U').toUpperCase();
  })();

  const accountStatus = () => {
    if (profile.is_blocked) return { label: 'Bloqueado', cls: 'bg-red-700/20 text-red-400 border border-red-500/20' };
    if (profile.is_active === false) return { label: 'Inativo', cls: 'bg-orange-500/20 text-orange-400 border border-orange-500/20' };
    return { label: 'Ativo', cls: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/20' };
  };

  const toggleBlock = async () => {
    setSaving(true);
    const newVal = !profile.is_blocked;
    await supabase.from('profiles').update({ is_blocked: newVal, updated_at: new Date().toISOString() }).eq('id', profile.id);
    onUpdate({ is_blocked: newVal });
    setSaving(false);
  };

  const toggleActive = async () => {
    setSaving(true);
    const newVal = profile.is_active === false;
    await supabase.from('profiles').update({ is_active: newVal, is_blocked: false, updated_at: new Date().toISOString() }).eq('id', profile.id);
    onUpdate({ is_active: newVal, is_blocked: false });
    setSaving(false);
  };

  const st = accountStatus();

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
      <div className="flex items-start gap-5">
        {/* Avatar */}
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600/40 to-violet-900/40 border border-violet-500/20 flex items-center justify-center shrink-0 overflow-hidden">
          {profile.avatar_url ? (
            <img src={profile.avatar_url} alt={profile.full_name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-[24px] font-700 text-violet-300">{initials}</span>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-[22px] font-700 text-white leading-tight">
                {profile.display_name || profile.full_name || <span className="text-white/30 italic">Sem nome</span>}
              </h2>
              {profile.display_name && profile.full_name && profile.display_name !== profile.full_name && (
                <p className="text-[13px] text-white/40 mt-0.5">{profile.full_name}</p>
              )}
              <p className="text-[14px] text-white/50 mt-1">{profile.email}</p>
              {profile.phone && <p className="text-[13px] text-white/40 mt-0.5">{profile.phone}</p>}
            </div>
            {/* Actions */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={toggleActive}
                disabled={saving}
                className={`px-3 py-1.5 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${profile.is_active !== false ? 'bg-orange-500/15 text-orange-400 hover:bg-orange-500/25' : 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25'}`}
              >
                <i className={`${profile.is_active !== false ? 'ri-pause-circle-line' : 'ri-play-circle-line'} mr-1.5`} />
                {profile.is_active !== false ? 'Desativar' : 'Ativar'}
              </button>
              <button
                onClick={toggleBlock}
                disabled={saving}
                className={`px-3 py-1.5 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${profile.is_blocked ? 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25' : 'bg-red-600/15 text-red-400 hover:bg-red-600/25'}`}
              >
                <i className={`${profile.is_blocked ? 'ri-lock-unlock-line' : 'ri-lock-line'} mr-1.5`} />
                {profile.is_blocked ? 'Desbloquear' : 'Bloquear'}
              </button>
            </div>
          </div>

          {/* Badges row */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className={`text-[11px] font-600 px-2.5 py-1 rounded-full ${st.cls}`}>{st.label}</span>
            {subscription && (
              <span className={`text-[11px] font-600 px-2.5 py-1 rounded-full ${subStatusColor[subscription.status] || 'bg-white/10 text-white/40'}`}>
                {subStatusLabel[subscription.status] || subscription.status}
              </span>
            )}
            <span className={`text-[11px] font-600 px-2.5 py-1 rounded-full border ${subscription?.plan_slug !== 'free' && subscription ? 'bg-amber-500/20 text-amber-400 border-amber-500/20' : 'bg-white/10 text-white/50 border-white/10'}`}>
              <i className="ri-vip-crown-line mr-1" />
              {subscription?.plan_name || 'Free'}
            </span>
            {profile.wizard_completed && (
              <span className="text-[11px] font-600 px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                <i className="ri-check-line mr-1" />Onboarding OK
              </span>
            )}
            {profile.auth_provider && (
              <span className="text-[11px] font-600 px-2.5 py-1 rounded-full bg-white/8 text-white/40 border border-white/10">
                <i className={`${profile.auth_provider === 'google' ? 'ri-google-line' : 'ri-mail-line'} mr-1`} />
                {profile.auth_provider === 'google' ? 'Google' : 'E-mail'}
              </span>
            )}
            {profile.marketing_opt_in && (
              <span className="text-[11px] font-600 px-2.5 py-1 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                <i className="ri-megaphone-line mr-1" />Marketing
              </span>
            )}
          </div>
        </div>

        {/* Quick meta */}
        <div className="text-right shrink-0 text-[12px] space-y-1.5 bg-white/3 border border-white/8 rounded-xl px-4 py-3">
          <p className="text-white/30">ID: <span className="font-mono text-white/50 text-[10px]">{profile.id.substring(0, 12)}...</span></p>
          <p className="text-white/30">Cadastro: <span className="text-white/60">{new Date(profile.created_at).toLocaleDateString('pt-BR')}</span></p>
          {profile.last_seen_at && (
            <p className="text-white/30">Último acesso: <span className="text-white/60">{new Date(profile.last_seen_at).toLocaleDateString('pt-BR')}</span></p>
          )}
          {profile.city && profile.state && (
            <p className="text-white/30">Cidade: <span className="text-white/60">{profile.city}/{profile.state}</span></p>
          )}
          {profile.lead_source && (
            <p className="text-white/30">Origem: <span className="text-white/60">{profile.lead_source}</span></p>
          )}
        </div>
      </div>

      {/* Tags row */}
      {profile.internal_tags && profile.internal_tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mt-4 pt-4 border-t border-white/8">
          <span className="text-[11px] text-white/30 mr-1">Tags:</span>
          {profile.internal_tags.map((tag) => (
            <span key={tag} className="text-[11px] bg-violet-600/20 text-violet-300 px-2 py-0.5 rounded-full border border-violet-500/20">{tag}</span>
          ))}
        </div>
      )}
    </div>
  );
}
